import React, { useState, useContext } from 'react';
import cx from 'classnames';

import { GridContainer, Row, Column } from '@xingternal/xing-grid';
import { ProfileImage } from '@xingternal/profile-image';
import { EditContext } from '@xingternal/pages-common';

import { BodyCopy } from '@xingternal/typography';
import { useLoginState } from '@xingternal/hops';
import { LOGIN_STATE } from '@xingternal/xing-frame-provider';

import CoverMedia from './CoverMedia';
import EntityInfo from './EntityInfo';
import SocialProof from './SocialProof';
import HeaderActions from './HeaderActions';
import HeaderSkeleton from './HeaderSkeleton';

import { EditLogo } from './Edit/Images';
import EditPageInfo from './Edit/PageInfo';
import TitleTooltip from './Edit/TitleTooltip';

import type { HeaderProps } from './types';

import styles from './Header.css';
import * as Styled from './Header.styled';

type HeaderActionsDataType = Pick<
  HeaderProps,
  | 'pageId'
  | 'isFollowingPage'
  | 'globalId'
  | 'pageUrl'
  | 'ctaType'
  | 'isSubpage'
> & {
  isEditor: boolean;
};

type LogoAreaProps = Pick<HeaderProps, 'logo' | 'title' | 'pageSlug'> & {
  isEditing?: boolean;
  newLogo?: string | null;
  newOriginalLogo?: string | null;
  setNewLogo?: (logo: any) => void;
  setNewOriginalLogo?: (logo: any) => void;
};

type PrimaryAreaProps = Pick<
  HeaderProps,
  'subtitle' | 'title' | 'pageSlug' | 'isSubpage' | 'isFreePage'
> & {
  isEditing?: boolean;
};

type SecondaryAreaProps = Pick<
  HeaderProps,
  'isSubpage' | 'showKununu' | 'pageId' | 'socialProof' | 'headerContent'
> & {
  headerActionsData: HeaderActionsDataType;
  isLoggedout: boolean;
  hasTertiaryArea: boolean;
};

type TertiaryAreaProps = Pick<HeaderProps, 'pageId' | 'socialProof'> & {
  headerActionsData: HeaderActionsDataType;
};

const LogoArea = ({
  isEditing,
  logo,
  newLogo,
  newOriginalLogo,
  pageSlug,
  setNewLogo,
  setNewOriginalLogo,
  title,
}: LogoAreaProps) => (
  <div className={styles.logoArea}>
    <ProfileImage
      profileName={title}
      src={newLogo || logo}
      type={'company'}
      variant="responsive"
      className={styles.logoImageContainer}
      data-cy="LOGO_IMAGE"
    />
    {isEditing && (
      <EditLogo
        setNewLogo={setNewLogo}
        newOriginalLogo={newOriginalLogo}
        setNewOriginalLogo={setNewOriginalLogo}
        pageSlug={pageSlug}
        hasLogo={!!logo}
      />
    )}
  </div>
);

const PrimaryArea = ({
  isEditing,
  isFreePage,
  isSubpage,
  subtitle,
  title,
}: PrimaryAreaProps) => (
  <div className={styles.primaryArea}>
    <div className={styles.titleContainer}>
      <Styled.Hero
        size={'small'}
        className={styles.title}
        data-cy={'entityPageTitle'}
        data-testid={!isSubpage ? 'PAGE_TITLE' : undefined}
      >
        {isSubpage && subtitle ? (
          <>
            <Styled.Subtitle>{subtitle}</Styled.Subtitle>
            <Styled.WhiteSpace>&nbsp;</Styled.WhiteSpace>
          </>
        ) : undefined}
        {title}
        {!isSubpage && <TitleTooltip />}
      </Styled.Hero>
    </div>
    {!isSubpage && (subtitle || isEditing) && (
      <div
        className={cx(styles.subtitleContainer, {
          [styles.editingSubtitleContainer]:
            isEditing && !isFreePage && !isSubpage,
        })}
        data-cy="ENTITY_PAGE_SLOGAN"
      >
        <BodyCopy
          size={'small'}
          noMargin
          sizeWide={'medium'}
          className={styles.subtitle}
          data-testid="PAGE_SLOGAN"
        >
          {subtitle}
        </BodyCopy>
        {!subtitle && isEditing && !isFreePage && (
          <EditPageInfo isLabel={true} />
        )}
        {isEditing && !isSubpage && !isFreePage && <EditPageInfo />}
      </div>
    )}
  </div>
);

const SecondaryArea = ({
  headerActionsData,
  isLoggedout,
  isSubpage,
  showKununu,
  pageId,
  socialProof,
  hasTertiaryArea,
  headerContent,
}: SecondaryAreaProps) => (
  <div className={styles.secondaryArea}>
    {!isSubpage && (
      <div className={styles.secondaryMainArea}>
        <EntityInfo
          headerContent={headerContent}
          condensed={isLoggedout || socialProof?.total === 0}
          showKununu={showKununu}
        />
      </div>
    )}
    {isSubpage && !isLoggedout && (
      <div className={styles.secondaryMainArea}>
        <SocialProof socialProof={socialProof} pageId={pageId} />
      </div>
    )}
    {!hasTertiaryArea && (
      <div className={styles.headerActionsArea}>
        <HeaderActions {...headerActionsData} />
      </div>
    )}
  </div>
);

const TertiaryArea = ({
  headerActionsData,
  pageId,
  socialProof,
}: TertiaryAreaProps) => (
  <div className={styles.tertiaryArea}>
    {socialProof && socialProof.total > 0 && (
      <div className={styles.tertiaryMainArea}>
        <SocialProof socialProof={socialProof} pageId={pageId} />
      </div>
    )}
    <div className={styles.headerActionsArea}>
      <HeaderActions {...headerActionsData} />
    </div>
  </div>
);

const Header = ({
  globalId,
  isFollowingPage,
  isFreePage,
  isPageLoading,
  isSubpage,
  showKununu,
  logo,
  pageId,
  pageSlug,
  pageUrl,
  socialProof,
  subtitle,
  title,
  ctaType,
  headerContent,
  coverMediaQueryResult,
}: HeaderProps) => {
  const [newLogo, setNewLogo] = useState(null);
  const [newOriginalLogo, setNewOriginalLogo] = useState(null);

  const { isEditing, isEditor } = useContext(EditContext);
  const loginState = useLoginState();
  const isLoggedout = loginState === LOGIN_STATE.loggedOut;

  const headerActionsData = {
    pageId,
    isFollowingPage,
    globalId,
    pageUrl,
    isEditor,
    ctaType,
    isSubpage,
  };

  const hasTertiaryArea = !(
    isLoggedout ||
    isSubpage ||
    socialProof?.total === 0
  );

  return (
    <div
      className={cx(styles.container, {
        [styles.noTertiaryArea]: !hasTertiaryArea,
        [styles.isLoggedout]: isLoggedout,
        [styles.isSubpage]: isSubpage,
        [styles.noSocialProof]: !(socialProof && socialProof?.total > 0),
      })}
    >
      <CoverMedia
        isPageLoading={isPageLoading}
        isFreePage={isFreePage}
        coverMediaQueryResult={coverMediaQueryResult}
      />
      <GridContainer className={styles.grid}>
        <Row>
          <Column size={12} className={styles.col}>
            {isPageLoading ? (
              <HeaderSkeleton />
            ) : (
              <>
                <div className={styles.topArea}>
                  <LogoArea
                    isEditing={isEditing}
                    logo={logo}
                    newLogo={newLogo}
                    newOriginalLogo={newOriginalLogo}
                    pageSlug={pageSlug}
                    setNewLogo={setNewLogo}
                    setNewOriginalLogo={setNewOriginalLogo}
                    title={title}
                  />
                  <div className={styles.sideArea}>
                    <PrimaryArea
                      isEditing={isEditing}
                      isFreePage={isFreePage}
                      isSubpage={isSubpage}
                      pageSlug={pageSlug}
                      subtitle={subtitle}
                      title={title}
                    />
                    <SecondaryArea
                      headerActionsData={headerActionsData}
                      isLoggedout={isLoggedout}
                      isSubpage={isSubpage}
                      showKununu={showKununu}
                      pageId={pageId}
                      socialProof={socialProof}
                      hasTertiaryArea={hasTertiaryArea}
                      headerContent={headerContent}
                    />
                  </div>
                </div>
                {hasTertiaryArea && (
                  <TertiaryArea
                    headerActionsData={headerActionsData}
                    pageId={pageId}
                    socialProof={socialProof}
                  />
                )}
              </>
            )}
          </Column>
        </Row>
      </GridContainer>
    </div>
  );
};

export default Header;
